/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import {
  Button,
  Form,
  Icon,
  SearchInput,
  Segment,
} from '@mairistem/composants';

import { useProfessions } from '@/hooks';
import type { Profession } from '@/hooks';

import ModalProfessions from './ModalProfessions';

/**
 * Résultat synthétique ajouté en fin de liste pour proposer « Créer XXX ».
 * `SearchInput` n'a pas de rappel « aucun résultat » : le même procédé
 * qu'`AdresseFormVoie` de la lib d'adresses.
 */
const TYPE_CREATION = 'creation';

interface ChampProfessionProps {
  label?: string;
  /** Émis dans le `onChange` ; accepte un chemin (`parent1.profession`). */
  name: string;
  value?: string;
  errors?: Record<string, string>;
  /** Clé d'erreur à lire dans `errors` (défaut : le dernier segment de `name`). */
  cleErreur?: string;
  disabled?: boolean;
  /** Largeur sur la grille de seize, si le champ vit dans un `Form.Group`. */
  width?: number;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
}

/**
 * Saisie assistée de la profession : recherche `contains` dans le référentiel,
 * « Créer XXX » si rien ne correspond, et un bouton ouvrant la liste complète.
 *
 * « Créer » ne fait AUCUN appel d'écriture : la colonne est un texte libre et
 * `AlimenteReferentielProfession` ajoute la profession au référentiel à
 * l'enregistrement de l'individu.
 *
 * ⚠️ La valeur est une chaîne, jamais un identifiant : sélectionner ou taper
 * produit la même donnée.
 */
const ChampProfession = (props: ChampProfessionProps) => {
  const {
    label = 'Profession',
    name,
    value,
    errors = {},
    cleErreur,
    disabled = false,
    width,
    onChange,
  } = props;

  const [recherche, setRecherche] = React.useState('');
  const [modaleOuverte, setModaleOuverte] = React.useState(false);

  const { professions, correspondExactement } = useProfessions(
    recherche,
    !_.isEmpty(recherche.trim()),
  );

  const erreur = errors?.[cleErreur ?? name.split('.').pop() ?? name];

  const emettre = React.useCallback(
    (libelle: string) => {
      onChange(null, { name, value: libelle });
    },
    [onChange, name],
  );

  const handleChange = React.useCallback(
    (_event: React.SyntheticEvent | null, payload: { value: string }) => {
      setRecherche(payload.value);
      // La saisie libre EST la valeur : on n'attend pas une sélection.
      emettre(payload.value);
    },
    [emettre],
  );

  /**
   * ⚠️ Deux contraintes de `SearchInput`, et les rater fait planter l'écran :
   *
   * 1. le résultat est dans le SECOND argument, enveloppé (`{ result }`) ;
   * 2. **le RETOUR devient la valeur affichée** — la lib fait
   *    `setState({ value: onSelect(e, data) })`. Ne rien retourner y met
   *    `undefined`, sur lequel Semantic lit `.length`.
   *
   * D'où le type de retour : toujours une chaîne.
   */
  const handleSelect = React.useCallback(
    (
      _evenement: React.SyntheticEvent,
      data?: { result?: Profession & { type?: string } },
    ): string => {
      const resultat = data?.result;

      if (_.isNil(resultat)) {
        return value ?? '';
      }

      const libelle =
        resultat.type === TYPE_CREATION ? recherche.trim() : resultat.libelle;

      if (_.isEmpty(libelle)) {
        return value ?? '';
      }

      emettre(libelle);
      setRecherche('');

      return libelle;
    },
    [emettre, recherche, value],
  );

  /** Croix d'effacement de la lib : la valeur du formulaire doit suivre. */
  const handleReset = React.useCallback(() => {
    setRecherche('');
    emettre('');
  }, [emettre]);

  // L'entrée de création n'apparaît pas sur une correspondance exacte : ce
  // serait proposer un doublon.
  const resultats = React.useMemo(() => {
    const terme = recherche.trim();

    if (_.isEmpty(terme) || correspondExactement(terme)) {
      return professions;
    }

    return [...professions, { type: TYPE_CREATION, libelle: terme }];
  }, [professions, recherche, correspondExactement]);

  const rendreResultat = React.useCallback(
    (resultat: Profession & { type?: string }) => (
      <Segment size="small" compact basic style={{ padding: 0 }}>
        {resultat?.type === TYPE_CREATION ? (
          <div data-testid="profession-creation">
            <Icon name="plus" />
            {`Créer « ${resultat.libelle} »`}
          </div>
        ) : (
          resultat?.libelle
        )}
      </Segment>
    ),
    [],
  );

  /*
   * ⚠️ `SearchInput` rend son propre icône à droite et transmet le reste de
   * `input` à un `Input` NU — pas à un `Form.Input`. Trois conséquences, d'où
   * la structure ci-dessous :
   *
   * - `label` y ferait une étiquette accolée à gauche → rendu par le
   *   `Form.Field`, qui met les libellés en gras au-dessus ;
   * - `action` ajouterait le bouton EN PLUS de l'icône de la lib → bouton posé
   *   à côté de l'input ;
   * - un tel `Input` ne s'étend pas sur la ligne → rangée flex.
   */
  return (
    <>
      <Form.Field error={!_.isNil(erreur)} width={width}>
        <label>{label}</label>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* `minWidth: 0` : sans lui un enfant flex refuse de se réduire. */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchInput
              input={{
                fluid: true,
                error: !_.isNil(erreur),
                'data-testid': `profession-input-${name}`,
              }}
              placeholder="Saisir ou rechercher une profession..."
              // `ai_profession` est un varchar(100).
              maxLength={100}
              defaultValue={value ?? ''}
              results={resultats}
              resultComponent={rendreResultat}
              noResultMessage="Aucune profession correspondante"
              disabled={disabled}
              onChange={handleChange}
              onSelect={handleSelect}
              onReset={handleReset}
              onBlur={() => setRecherche('')}
            />
          </div>
          {/* Coins gauches carrés : le bouton se lit comme accolé à l'input. */}
          <Button
            type="button"
            basic
            disabled={disabled}
            data-testid={`profession-liste-${name}`}
            style={{
              marginLeft: '-1px',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              // Le SVG de Lucide n'a pas la boîte d'une icône Semantic.
              padding: '0 1em',
            }}
            onClick={() => setModaleOuverte(true)}
          >
            {/* Même icône que le champ des professions du recensement. */}
            <Icon
              name="ListFilter"
              iconSet="Lucide"
              size={16}
              style={{ marginRight: 0 }}
            />
          </Button>
        </div>
      </Form.Field>
      <ModalProfessions
        open={modaleOuverte}
        onClose={() => setModaleOuverte(false)}
        onSelect={emettre}
      />
    </>
  );
};

export default ChampProfession;
